import { Typography } from "@/deps/ui-library";
import type { Totals } from "@/lib/invoiceTotals";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

/**
 * The totals block (BG-22), in the order the standard defines and with the business terms visible.
 *
 * The BT numbers are shown deliberately. When a document is rejected by a receiving network the
 * error names a business term — "BR-CO-13: BT-109 must equal BT-106 − BT-107 + BT-108" — and a
 * totals block that shows only labels leaves whoever is reading it to guess which line that is.
 */
export function TotalsBlock({
  totals,
  /** Withholding has no home in a BIS payload; it is shown here because it changes what is due. */
  withholding,
  currency = "NPR",
  showDue = true,
}: {
  totals: Totals;
  withholding?: { amount: string; label: string };
  currency?: string;
  /** Off for a document nothing is owed on yet, such as a quotation. */
  showDue?: boolean;
}) {
  const say = useTaxWords();
  const rows: Array<{
    bt: string;
    label: string;
    value: string;
    emphasis?: boolean;
    muted?: boolean;
  }> = [{ bt: "BT-106", label: "Sum of line amounts", value: totals.sumOfLineNetAmounts }];

  // Allowances and charges are shown only when there are any: two rows of 0.00 on every invoice
  // train people to stop reading the block.
  if (totals.sumOfAllowances !== "0.00") {
    rows.push({
      bt: "BT-107",
      label: "Document allowances",
      value: `−${formatAmount(totals.sumOfAllowances)}`,
    });
  }
  if (totals.sumOfCharges !== "0.00") {
    rows.push({
      bt: "BT-108",
      label: "Document charges",
      value: formatAmount(totals.sumOfCharges),
    });
  }

  rows.push(
    { bt: "BT-109", label: say("Total without VAT"), value: totals.totalWithoutVat },
    { bt: "BT-110", label: say("Total VAT"), value: totals.totalVatAmount },
    {
      bt: "BT-112",
      label: say(`Total with VAT (${currency})`),
      value: totals.totalWithVat,
      emphasis: true,
    },
  );

  if (totals.paidAmount !== "0.00") {
    rows.push({
      bt: "BT-113",
      label: "Already paid",
      value: `−${formatAmount(totals.paidAmount)}`,
    });
  }
  if (totals.roundingAmount !== "0.00") {
    // BT-114 exists so a seller can round to a convenient figure. It is shown, never absorbed.
    rows.push({ bt: "BT-114", label: "Rounding", value: totals.roundingAmount });
  }
  if (withholding && withholding.amount !== "0.00") {
    rows.push({
      bt: "—",
      label: withholding.label,
      value: `−${formatAmount(withholding.amount)}`,
      muted: true,
    });
  }

  const due = withholding
    ? subtractForDisplay(totals.amountDueForPayment, withholding.amount)
    : totals.amountDueForPayment;

  if (showDue) rows.push({ bt: "BT-115", label: "Amount due", value: due, emphasis: true });

  return (
    <dl className="text-sm">
      {rows.map((row) => (
        <div
          key={row.bt + row.label}
          className={[
            "border-muted flex items-baseline justify-between gap-4 border-b py-1.5 last:border-0",
            row.emphasis ? "font-semibold" : "",
          ].join(" ")}
        >
          <dt className={row.muted ? "text-muted-foreground" : "text-foreground"}>
            {row.label}
            <span className="text-muted-foreground ml-1.5 text-xs">{row.bt}</span>
          </dt>
          <dd
            className={["num", row.emphasis ? "text-foreground" : "text-muted-foreground"].join(
              " ",
            )}
          >
            {row.value.startsWith("−") ? row.value : formatAmount(row.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Display arithmetic only. The authoritative figure comes from the chain. */
function subtractForDisplay(amount: string, withheld: string): string {
  const cents = Math.round(Number(amount) * 100) - Math.round(Number(withheld) * 100);
  return (cents / 100).toFixed(2);
}

/**
 * The VAT breakdown (BG-23): derived, non-editable, and sitting between the lines and the totals.
 *
 * The brief calls this "the single most useful piece of drill-down on the whole document" — it is
 * how somebody checks that their lines rolled up the way they meant.
 */
export function VatBreakdownTable({ totals }: { totals: Totals }) {
  const say = useTaxWords();
  if (totals.breakdown.length === 0) return null;

  return (
    <table className="w-full border-collapse text-sm">
      <caption className="mb-1 text-left">
        <Typography.Text as="span" variant="small" weight="medium" muted>
          {say("VAT breakdown")}
        </Typography.Text>
      </caption>
      <thead>
        <tr className="border-border border-b">
          <th scope="col" className="text-muted-foreground px-2 py-1 text-left text-xs font-medium">
            Category
          </th>
          <th
            scope="col"
            className="text-muted-foreground px-2 py-1 text-right text-xs font-medium"
          >
            Rate
          </th>
          <th
            scope="col"
            className="text-muted-foreground px-2 py-1 text-right text-xs font-medium"
          >
            Taxable
          </th>
          <th
            scope="col"
            className="text-muted-foreground px-2 py-1 text-right text-xs font-medium"
          >
            {say("VAT")}
          </th>
        </tr>
      </thead>
      <tbody>
        {totals.breakdown.map((row) => (
          <tr
            key={`${row.category}-${row.rate ?? "none"}`}
            className="border-muted border-b last:border-0"
          >
            <td className="text-foreground px-2 py-1">
              {CATEGORY_LABELS[row.category] ?? row.category}
            </td>
            {/* Out of scope carries no rate at all — an em dash, not "0%", because they mean
                different things on a return. */}
            <td className="num text-muted-foreground px-2 py-1 text-right">
              {row.rate === null ? "—" : `${row.rate}%`}
            </td>
            <td className="num text-foreground px-2 py-1 text-right">
              {formatAmount(row.taxableAmount)}
            </td>
            <td className="num text-foreground px-2 py-1 text-right">
              {formatAmount(row.taxAmount)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Labels that a non-accountant can tell apart. Zero-rated and exempt are conflated constantly and
 * are not the same thing: zero-rated allows input recovery, exempt does not.
 */
const CATEGORY_LABELS: Record<string, string> = {
  S: "Standard rate",
  Z: "Zero-rated",
  E: "Exempt",
  AE: "Reverse charge",
  O: "Outside scope",
};
